import React, { useCallback, useState } from 'react';
import { cpOrderInvoicesListColumnSpec, cpOrderInvoicesListAllColumnsView } from './CpOrderInvoicesListTableSpec';
import { useCpOrderInvoicesListTableInit } from './useCpOrderInvoicesListTableInit';
import { useCpOrderInvoicesListTableState } from './useCpOrderInvoicesListTableState';
import TableTop from '../../../../core/table/TableTop';
import TableBody from '../../../../core/table/TableBody';
import TableHead from '../../../../core/table/TableHead';
import TablePaging from '../../../../core/table/TablePaging';
import { SavedViewsFlyOutMode } from '../../../../core/table/TableViewEditForm';
import { useTableViewSelection } from '../../../../core/table/hooks/useTableViewSelection';
import { useSetImmerAtom } from 'jotai-immer';
import { convertToLegacyTableViews } from '../../../../core/table/utils/tableViewHelpers';
import { useTableViewPersistence } from '../../../../core/table/hooks/useTableViewPersistence';
import { getCpOrderInvoicesProgAdminView } from './getCpOrderInvoicesProgAdminView';
import { CpOrderInvoicesDataItem } from '../cp_order_invoices_list_types';
import { cpOrderInvoicesTableStateAtom } from '../cp_order_invoices_list_atoms';
import TableExceedsDataSizeMessage from '../../../../core/table/TableExceedsDataSizeMessage';
import { useCpOrderInvoicesStore } from '../pagehooks/useCpOrderInvoicesStore';
import { useCpOrderInvoicesData } from './useCpOrderInvoicesData';
import SaveToExcelButton from '../../../../core/table/actions/SaveToExcelButton';
import HelpButton from '../../../../core/components/HelpButton';
import { HttpErrorIgnore } from '../../../../../global';
import cpOrderInvoicesRepo from '../../data/cpOrderInvoicesRepo';
import { useCpOrderInvoicesSearchState } from '../search/useCpOrderInvoicesSearchState';
import { CpOrderInvoicesSearchModel } from '../search/cp_order_invoices_search_types';
import TableList from '../../../../core/table/TableList';

export const cpOrderInvoicesListTableId = 'cpOrderInvoicesList';

type CpOrderInvoicesListTableProps = {
    showTableViewEditor?: boolean;
    isExternalFlyOutOpen?: boolean;
};

const onGetExcel = (params: CpOrderInvoicesSearchModel): Promise<HttpErrorIgnore | undefined> =>
    cpOrderInvoicesRepo.getExcel(params);

const CpOrderInvoicesListTable = ({
    showTableViewEditor = false,
    isExternalFlyOutOpen = false,
}: CpOrderInvoicesListTableProps): JSX.Element => {
    const { init } = useCpOrderInvoicesListTableInit();

    if (init) {
        return <></>;
    }

    return (
        <TableWrapper
            showTableViewEditor={showTableViewEditor}
            isExternalFlyOutOpen={isExternalFlyOutOpen}
        />
    );
};

type TableWrapperProps = CpOrderInvoicesListTableProps;

const TableWrapper = ({
    // showTableViewEditor,
    isExternalFlyOutOpen = false,
}: TableWrapperProps): JSX.Element => {
    /* eslint-disable-next-line */
    const [showFlyOut, setShowFlyOut] = useState(false);
    const [flyOutMode, setFlyOutMode] = useState<SavedViewsFlyOutMode>('CREATE');
    const setTableState = useSetImmerAtom(cpOrderInvoicesTableStateAtom);
    const { navigateToDetailsPage } = useCpOrderInvoicesStore();
    const {
        fltStats,
        paging,
        sort,
        filters,
        currentViewId,
        waiting,
        showHelp,
        setPaging,
        setFilter,
        setSort,
        setCurrentViewId,
        setWait,
        views: rawTableViews,
    } = useCpOrderInvoicesListTableState();
    const { state } = useCpOrderInvoicesSearchState();
    const { items, rawDataSize } = useCpOrderInvoicesData();
    const { action, loading } = useCpOrderInvoicesStore();

    const legacyViews = convertToLegacyTableViews(rawTableViews);
    const { tableViews } = useTableViewPersistence({
        tableViews: legacyViews,
        tableId: cpOrderInvoicesListTableId,
        setCurrentViewId,
        setTableState,
        flyOutMode,
        setFlyOutMode,
        setShowFlyOut,
        colMap: cpOrderInvoicesListColumnSpec(),
        defaultView: cpOrderInvoicesListAllColumnsView(),
    });

    const { viewFilteredColumns, viewFilteredSections } = useTableViewSelection({
        tableId: cpOrderInvoicesListTableId,
        tableViews,
        currentViewId,
        setCurrentViewId,
        columnSpec: cpOrderInvoicesListColumnSpec,
        defaultView: cpOrderInvoicesListAllColumnsView,
        allColumnsView: getCpOrderInvoicesProgAdminView,
        setTableState,
    });

    const isTableDisabled = isExternalFlyOutOpen;

    const handleRowClick = useCallback(
        (row: CpOrderInvoicesDataItem): void => {
            navigateToDetailsPage(row, false);
        },
        [navigateToDetailsPage],
    );

    if (action !== 'LIST' || loading) {
        return <></>;
    }

    const handleHelpLinkClick = (): void => {
        showHelp();
    };

    return (
        <TableList noSpacing>
            <TableExceedsDataSizeMessage dataSize={rawDataSize} />
            <TableTop
                id={cpOrderInvoicesListTableId}
                fltStats={fltStats}
                topRight={
                    <div>
                        <SaveToExcelButton
                            waiting={waiting}
                            setWait={setWait}
                            onGetExcel={() => onGetExcel(state)}
                        />
                        <HelpButton onClick={handleHelpLinkClick} />
                    </div>
                }
            />
            <table className={isTableDisabled ? 'disabled-interaction' : ''}>
                <TableHead
                    cols={viewFilteredColumns}
                    sections={viewFilteredSections}
                    sort={sort}
                    filters={filters}
                    setFilter={setFilter}
                    setSort={setSort}
                />
                <TableBody
                    cols={viewFilteredColumns}
                    rows={items}
                    onClick={isTableDisabled ? undefined : handleRowClick}
                />
            </table>
            <TablePaging
                paging={paging}
                setPaging={setPaging}
                fltStats={fltStats}
            />
        </TableList>
    );
};

export default CpOrderInvoicesListTable;
